import type { PrebuildReviewState } from "@/lib/api/databook";

export interface BuilderStageNavigationItem {
  key: string;
  label: string;
  disabled?: boolean;
}

export const DATABOOK_BUILDER_STAGES = [
  { key: "upload", label: "Upload" },
  { key: "coverage", label: "Update RFI" },
  { key: "ents", label: "Legal entities" },
  { key: "basis", label: "Basis of preparation" },
  { key: "book", label: "Databook" },
  { key: "files", label: "File review" },
  { key: "qna", label: "Q&A" },
  { key: "adjustments", label: "Adjustment Summary" },
  { key: "adjusted-databook", label: "Adjusted Databook" },
  { key: "report", label: "Report" },
] as const satisfies readonly BuilderStageNavigationItem[];

export const CONNECTED_DATABOOK_BUILDER_STAGES = [
  { key: "upload", label: "Upload" },
  { key: "coverage", label: "Update RFI" },
  { key: "ents", label: "Legal entities" },
  { key: "questions", label: "Questions" },
  { key: "basis", label: "Basis of preparation" },
  { key: "book", label: "Databook" },
  { key: "files", label: "File review" },
  { key: "qna", label: "Q&A" },
  { key: "adjustments", label: "Adjustment Summary" },
  { key: "adjusted-databook", label: "Adjusted Databook" },
  { key: "report", label: "Report" },
] as const satisfies readonly BuilderStageNavigationItem[];

export type ConnectedDatabookBuilderStage =
  (typeof CONNECTED_DATABOOK_BUILDER_STAGES)[number]["key"];

export function connectedDatabookBuilderStages(
  showQuestions: boolean,
  showCoverage: boolean,
  availability: { hasAnalysis: boolean; hasDatabook: boolean; hasQuestions: boolean },
): readonly BuilderStageNavigationItem[] {
  return CONNECTED_DATABOOK_BUILDER_STAGES.filter(
    (stage) =>
      (showQuestions || stage.key !== "questions")
      && (showCoverage || stage.key !== "coverage"),
  ).map((stage) => {
    if (stage.key !== "upload" && !availability.hasAnalysis) {
      return { ...stage, disabled: true };
    }
    if (stage.key === "qna" && !availability.hasQuestions) {
      return { ...stage, disabled: true };
    }
    if (["adjustments", "adjusted-databook", "report"].includes(stage.key) && !availability.hasDatabook) {
      return { ...stage, disabled: true };
    }
    return stage;
  });
}

export function shouldShowQuestionsStage(
  state: PrebuildReviewState | undefined,
  batchId: string | null | undefined,
  questionCount: number,
): boolean {
  return Boolean(
    batchId
    && questionCount > 0
    && state
    && ["questions_required", "running", "failed", "ready"].includes(state),
  );
}

export function postAnalysisBuilderStage(
  recommendNotBuild: boolean,
): ConnectedDatabookBuilderStage {
  if (recommendNotBuild) return "coverage";
  return "ents";
}

export function postLegalEntitiesBuilderStage(
  showQuestions: boolean,
): ConnectedDatabookBuilderStage {
  return showQuestions ? "questions" : "basis";
}

type BuilderStageStorage = Pick<Storage, "getItem" | "setItem">;

const CONNECTED_STAGE_KEYS = new Set<string>(
  CONNECTED_DATABOOK_BUILDER_STAGES.map((stage) => stage.key),
);

export function builderStageFromQuery(
  stage: string | null,
): ConnectedDatabookBuilderStage | null {
  return stage && CONNECTED_STAGE_KEYS.has(stage)
    ? stage as ConnectedDatabookBuilderStage
    : null;
}

function storageKey(projectId: string): string {
  return `dilly.databookBuilder.lastStage.${projectId}`;
}

export function loadLastBuilderStage(
  projectId: string | undefined,
  storage?: BuilderStageStorage,
): ConnectedDatabookBuilderStage | null {
  if (!projectId) return null;
  try {
    const savedStage = (storage ?? window.localStorage).getItem(
      storageKey(projectId),
    );
    return savedStage && CONNECTED_STAGE_KEYS.has(savedStage)
      ? savedStage as ConnectedDatabookBuilderStage
      : null;
  } catch {
    return null;
  }
}

export function saveLastBuilderStage(
  projectId: string | undefined,
  stage: string,
  storage?: BuilderStageStorage,
): void {
  if (!projectId || !CONNECTED_STAGE_KEYS.has(stage)) return;
  try {
    (storage ?? window.localStorage).setItem(storageKey(projectId), stage);
  } catch {
    // Browsers may disable or limit local storage.
  }
}
